<script setup lang="ts">
// components
import CiCampsiteMediaInfo from '@/components/CiCampsiteMedia/CiCampsiteMediaInfo.vue';

/********************
 * REFS & VARS       *
 ********************/
export interface CiCampsiteMediaImageProps {
  item: any;
  selected?: boolean;
  showUser?: boolean;
  altFallback?: string;
}
const props = withDefaults(defineProps<CiCampsiteMediaImageProps>(), {
  selected: false,
  showUser: true,
});

/********************
 * COMPOSITIONS      *
 ********************/
const { fixImageUrlAndCheckWebp } = useWebp();

/********************
 * REFS & VARS       *
 ********************/
const sizes = {
  small: [650, 433],
  big: [1110, 740],
};

const description = computed(() => {
  return props.item.description || `${props.altFallback} image ${props.item.idx}`;
});

/********************
 * FUNCTIONS         *
 ********************/
function getPicture(selected = false) {
  const smallImg = props.item.images.small;
  const bigImg = props.item.images.big;
  const [smallWidth, smallHeight] = sizes.small;
  const [bigWidth, bigHeight] = sizes.big;
  const smallJpg = fixImageUrlAndCheckWebp(smallImg);
  const smallWebp = fixImageUrlAndCheckWebp(smallImg, true);
  const bigJpg = fixImageUrlAndCheckWebp(bigImg);
  const bigWebp = fixImageUrlAndCheckWebp(bigImg, true);

  const selectedPictures = [
    {
      srcset: `${bigWebp.url} ${bigWidth}w`,
      media: '(min-width: 992px)',
      type: bigWebp.contentType,
      width: bigWidth,
      height: bigHeight,
    },
    {
      srcset: `${bigJpg.url} ${bigWidth}w`,
      media: '(min-width: 992px)',
      type: bigJpg.contentType,
      width: bigWidth,
      height: bigHeight,
    },
    {
      srcset: `${smallWebp.url} ${smallWidth}w`,
      media: '(min-width: 0px)',
      type: smallWebp.contentType,
      width: smallWidth,
      height: smallHeight,
    },
    {
      srcset: `${smallJpg.url} ${smallWidth}w`,
      media: '(min-width: 0px)',
      type: smallJpg.contentType,
      width: smallWidth,
      height: smallHeight,
    },
  ];

  if (selected) {
    return selectedPictures;
  }
  return [
    {
      srcset: `${smallWebp.url} ${smallWidth}w`,
      media: '(min-width: 1200px)',
      type: smallWebp.contentType,
      width: smallWidth,
      height: smallHeight,
    },
    {
      srcset: `${smallJpg.url} ${smallWidth}w`,
      media: '(min-width: 1200px)',
      type: smallJpg.contentType,
      width: smallWidth,
      height: smallHeight,
    },
    ...selectedPictures,
  ];
}
</script>

<template>
  <div
    class="media-type-picture relative overflow-hidden rounded pt-[66.61%]"
    :class="{ 'selected': props.selected, 'bg-gray-10': !props.selected }"
  >
    <template v-if="!props.item.eager">
      <client-only>
        <picture>
          <source
            v-for="(source, idx) of getPicture()"
            :key="idx"
            :srcset="source.srcset"
            :media="source.media"
            :type="source.type"
          />

          <img
            :src="props.item.images.small"
            :width="sizes.small[0]"
            :height="sizes.small[1]"
            class="image absolute left-0 top-0 size-full"
            loading="lazy"
            :alt="description"
          />
        </picture>
      </client-only>
    </template>
    <picture v-else>
      <source
        v-for="(source, idx) of getPicture()"
        :key="idx"
        :srcset="source.srcset"
        :media="source.media"
        :type="source.type"
      />

      <img
        :src="item.images.small"
        :width="sizes.small[0]"
        :height="sizes.small[0]"
        class="image absolute left-0 top-0 size-full"
        :alt="description"
        loading="eager"
      />
    </picture>

    <!-- selected -->
    <picture v-if="selected">
      <source
        v-for="(source, idx) of getPicture(true)"
        :key="idx"
        :srcset="source.srcset"
        :media="source.media"
        :type="source.type"
      />
      <img
        :src="item.images.small"
        :width="sizes.small[0]"
        :height="sizes.small[1]"
        class="image absolute left-0 top-0 h-auto w-full rounded"
        :alt="description"
        loading="eager"
      />
    </picture>
    <CiCampsiteMediaInfo
      v-if="props.selected"
      :item="props.item"
      :show-user="props.showUser"
    />
  </div>
</template>

<style scoped>
.media-type-picture.selected .image {
  height: auto !important;
}
</style>
